import { FC, useContext } from "react";
import {
  Box,
  CircularProgress,
  makeStyles,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Alert } from "@material-ui/lab";
import { Link } from "react-router-dom";

import { Button, TagList } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { baseContainerMaxWidthStyles } from "containers/BaseContainer";
import { AppContext } from "contexts";
import * as routes from "routes";
import {
  ACCEPTING_OFFERS_STATES,
  SECONDARY_LISTING,
} from "services/listings/constants";
import { useListingInfos } from "services/listings/hooks";
import theme from "theme";
import { Theme } from "theme";
import { linkPurpleStyles } from "theme/constants";

import HeaderActionButton from "./HeaderActionButton";
import HeaderImage from "./HeaderImage";
import HiddenContent from "./HiddenContent";
import ListingContent from "./ListingContent";
import WebsocketStatus from "./WebsocketStatus";

const thirdRowHeight = 360;

const useStyles = makeStyles<Theme, { displayHiddenContent: boolean }>({
  container: {
    background: `radial-gradient(50% 50% at 50% 50%, ${theme.custom.palette.purple.main} 0%, #4638AB 51.56%, #2A313B 100%)`,
    color: theme.custom.palette.softWhite.main,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    ...baseContainerMaxWidthStyles,
    color: theme.palette.primary.contrastText,
    padding: "2rem",
  },
  link: linkPurpleStyles,
  fistRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  secondRow: {
    display: "flex",
  },
  id: {
    fontSize: "0.875rem",
  },
  title: {
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.8rem",
    },
  },
  centerDownXs: {
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
    },
  },
  thirdRow: {
    display: "flex",
    justifyContent: "space-between",
    minHeight: thirdRowHeight,
    [theme.breakpoints.up("md")]: {
      justifyContent: "space-around",
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      height: "80% !important",
    },
  },
  hideButton: {
    visibility: "hidden",
    disabled: true,
  },
});

interface AssetDetailHeaderProps {
  isLoading: boolean;
  listingId: number;
  isModal?: boolean;
}

const AssetDetailHeader: FC<AssetDetailHeaderProps> = ({
  isLoading,
  listingId,
  isModal,
}) => {
  const { user } = useContext(AppContext);
  const { asset, listing } = useAssetDetailData(listingId);
  const { id: assetId } = asset || {};

  const {
    accept_final_offers,
    buy_now_price,
    deal,
    featured_source,
    hidden,
    is_featured,
    is_in_feature_queue,
    is_owner,
    kind,
    owner_last_active_date,
    published_date,
    state,
    tags,
    title,
  } = listing || {};

  const fallbackTitle = !!listingId
    ? `Listing ID (${listingId})`
    : `Asset ID (${assetId})`;

  const [isNewListing, isActiveOwner] = useListingInfos(
    published_date,
    owner_last_active_date,
  );

  const isOpenListing = state === "open";
  const isAcceptingFinalOffers = state === "accepting_final_offers";

  const backButtonTo = !!listing
    ? isOpenListing || isAcceptingFinalOffers
      ? routes.orderBookOpenListings(user?.userId)
      : routes.orderBookPastDeals(user?.userId)
    : "";

  const isOtherHiddenState = ["closed", "expired"].includes(state);
  const displayHiddenContent = !listingId || hidden || isOtherHiddenState;
  const classes = useStyles({ displayHiddenContent });

  return (
    <Box className={classes.container} data-testid="asset-detail-header">
      {isLoading ? (
        <Box height="700px" display="flex" alignItems="center">
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box className={classes.content}>
          <Box className={classes.fistRow}>
            <Button
              style={{ paddingLeft: 0 }}
              startIcon={<ArrowBackIcon />}
              customColor="white"
              variant="text"
              size="large"
              component={Link}
              to={backButtonTo}
              className={isModal ? classes.hideButton : ""}
            >
              Back to The Exchange
            </Button>
            {!!listingId && !hidden && !isOtherHiddenState && (
              <HeaderActionButton
                isBuyNowPriceSet={!!buy_now_price}
                isFeatured={is_featured}
                isInFeatureQueue={is_in_feature_queue}
                isOwner={is_owner}
                isSecondaryListing={kind === SECONDARY_LISTING}
                featuredSource={featured_source}
                hasDeal={!!deal}
                listingId={listingId}
                listingState={state}
              />
            )}
          </Box>
          {accept_final_offers && ACCEPTING_OFFERS_STATES.includes(state) && (
            <Box my={1}>
              <Alert icon={false} severity="warning">
                Please Note: This asset includes a new marketplace feature. Once
                an offer is accepted, there will be a 24-hour auction allowing
                anyone who has previously placed a bid on this asset (+
                All-Access Members) an opportunity to make final offers. At the
                end of the auction, the bidder with the highest offer will win
                this asset.{" "}
                <a
                  href="https://royaltyexchange.com/blog/accepting-final-offers-new-exchange-feature-an-overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.link}
                >
                  <strong> Click here for more information</strong>
                </a>
                .
              </Alert>
            </Box>
          )}
          <Box className={classes.secondRow}>
            <Box>
              <Typography
                variant="h1"
                className={`${classes.centerDownXs} ${classes.title}`}
              >
                {title || fallbackTitle}
              </Typography>
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                gridGap={`${theme.spacing(1)}px`}
              >
                <Typography
                  variant="h3"
                  className={`${classes.id} ${classes.centerDownXs}`}
                >
                  {fallbackTitle}
                </Typography>
                <WebsocketStatus listingId={listingId} />
              </Box>
              <Box mt={2}>
                <TagList
                  tags={tags}
                  isFeatured={is_featured}
                  isActiveOwner={isActiveOwner && isOpenListing}
                  isNewListing={isNewListing}
                />
              </Box>
            </Box>
          </Box>
          <Box className={classes.thirdRow}>
            <HeaderImage listing={listing} />
            {displayHiddenContent ? (
              <HiddenContent />
            ) : (
              <ListingContent isModal={!!isModal} listingId={listingId} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AssetDetailHeader;
